import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Search, Star } from 'lucide-react';
import { venueService } from '@/lib/api/venue.service';
import type { GooglePlaceSearchResult, Venue } from '@/types/venue.types';

interface AutocompleteSuggestion extends GooglePlaceSearchResult {
  _db_venue_id?: number;
}

interface VenueAutocompleteProps {
  value?: GooglePlaceSearchResult | null;
  onChange: (venue: GooglePlaceSearchResult | null) => void;
  placeholder?: string;
  className?: string;
  locationBias?: { lat: number; lng: number; radius_miles?: number };
}

export const VenueAutocomplete: React.FC<VenueAutocompleteProps> = ({
  value,
  onChange,
  placeholder = "Search for a venue...",
  className = "",
  locationBias
}) => {
  const [query, setQuery] = useState(value?.name || '');
  const [suggestions, setSuggestions] = useState<GooglePlaceSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (value) {
      setQuery(value.name);
    }
  }, [value]);

  const searchVenues = async (searchQuery: string) => {
    if (!searchQuery.trim() || searchQuery.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const locationString = locationBias 
        ? `${locationBias.lat},${locationBias.lng}` 
        : undefined;
        
      const radiusMeters = locationBias?.radius_miles 
        ? locationBias.radius_miles * 1609.34 
        : undefined;

      const results = await venueService.searchVenues({
        query: searchQuery,
        location: locationString,
        radiusMiles: radiusMeters ? radiusMeters / 1609.34 : undefined,
      });
      let suggestions: AutocompleteSuggestion[] = [];
      // Map saved DB venues to GooglePlaceSearchResult shape (google_place_id → place_id)
      if (results.saved_venues && Array.isArray(results.saved_venues)) {
        suggestions = suggestions.concat(
          results.saved_venues.map((v: Venue) => ({
            place_id: v.google_place_id,
            _db_venue_id: v.id, // fast path: skip ensureVenue for already-saved venues
            name: v.name,
            formatted_address: v.address || undefined,
            types: v.place_types || [],
            rating: v.rating,
            user_ratings_total: undefined,
            geometry: v.geometry as GooglePlaceSearchResult['geometry'] | undefined,
          }))
        );
      }
      // Google suggestions already have place_id
      if (results.google_suggestions && Array.isArray(results.google_suggestions)) {
        suggestions = suggestions.concat(results.google_suggestions);
      }
      setSuggestions(suggestions);
      setShowSuggestions(true);
    } catch {
      setError('Failed to search venues');
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newQuery = e.target.value;
    setQuery(newQuery);

    // Clear previous selection if input changes
    if (value && newQuery !== value.name) {
      onChange(null);
    }

    // Debounce search
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      searchVenues(newQuery);
    }, 300);
  };

  const handleSuggestionClick = (suggestion: GooglePlaceSearchResult) => {
    setQuery(suggestion.name);
    setShowSuggestions(false);
    onChange(suggestion);
  };

  const handleInputFocus = () => {
    if (suggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  const formatTypes = (types: string[]) => {
    const relevantTypes = types.filter(type => 
      !['point_of_interest', 'establishment'].includes(type)
    );
    return relevantTypes.slice(0, 2).map(type => 
      type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    ).join(', ');
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <MapPin className="h-4 w-4 text-gray-400" />
        </div>
        <input
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder={placeholder}
          className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
          {isLoading ? (
            <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full" />
          ) : (
            <Search className="h-4 w-4 text-gray-400" />
          )}
        </div>
      </div>

      {error && (
        <div className="mt-1 text-sm text-red-600">
          {error}
        </div>
      )}

      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
          {suggestions.map((suggestion) => (
            <div
              key={suggestion.place_id}
              onClick={() => handleSuggestionClick(suggestion)}
              className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-gray-900 truncate">
                    {suggestion.name}
                  </h4>
                  {suggestion.formatted_address && (
                    <p className="text-sm text-gray-600 truncate mt-1">
                      {suggestion.formatted_address}
                    </p>
                  )}
                  {suggestion.types && suggestion.types.length > 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      {formatTypes(suggestion.types)}
                    </p>
                  )}
                </div>
                <div className="flex items-center ml-3 space-x-2">
                  {suggestion.rating && (
                    <div className="flex items-center text-xs text-gray-600">
                      <Star className="h-3 w-3 text-yellow-400 mr-1" />
                      {suggestion.rating}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showSuggestions && suggestions.length === 0 && !isLoading && query.length >= 2 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
          <div className="px-4 py-3 text-sm text-gray-500 text-center">
            No venues found. Try a different search term.
          </div>
        </div>
      )}
    </div>
  );
};
